import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { AngularFireRemoteConfig } from '@angular/fire/compat/remote-config';
import { ScanResult } from '../models/scan-result';
import { ConfigService } from './config.service';
import { CodeDetection, Configuration, StrichSDK } from '@pixelverse/strichjs-sdk';
import * as Sentry from '@sentry/browser';

@Injectable({
  providedIn: 'root'
})
export class ScannerService {

  resolutionPreference: string;
  scannerInactivityTimeout: number;
  currentTourName = new BehaviorSubject<string | null>(null);

  // SDK initialization state
  sdkInitialized = new BehaviorSubject<boolean | undefined>(undefined);
  scanResult = new Subject<ScanResult>();
  scanningPaused = new BehaviorSubject<boolean>(false);

  private resolutionPreferenceSubscription: Subscription;
  private readonly duplicateFilterInterval = 2500;


  constructor(
    private remoteConfig: AngularFireRemoteConfig,
    private config: ConfigService,
  ) {
    this.currentTourName.subscribe((tourNameOrNull) => {
      StrichSDK.setCustomId(tourNameOrNull);
    });
    this.resolutionPreferenceSubscription = this.remoteConfig.strings.resolutionPreference.subscribe(v => {
      console.log(`Got resolutionPreference = ${v} from RemoteConfig`);
      this.resolutionPreference = v;
    });

    remoteConfig.numbers.scannerInactivityTimeout.subscribe(v => {
      console.log(`Got scannerInactivityTimeout = ${v} from RemoteConfig`);
      this.scannerInactivityTimeout = v;
    });

    // @ts-expect-error enable debugging output
    StrichSDK._debug = environment.strichDebug;

    if (!StrichSDK.isInitialized()) {
      console.debug(`Initializing STRICH SDK...`);
      StrichSDK.initialize(environment.strichLicenseKey)
        .then(() => {
          console.debug(`STRICH SDK initialization completed successfully`);
          this.sdkInitialized.next(true);
        })
        .catch((error) => {
          console.error(`Failed to initialize STRICH SDK: ${error}`);
          Sentry.captureException(error);
          this.sdkInitialized.next(false);
        });
    }
  }

  publishScanResult(codeDetection: CodeDetection) {
    this.scanResult.next({
      symbology: codeDetection.typeName,
      data: codeDetection.data
    });
  }

  strichConfiguration(hostElement: HTMLElement): Configuration {

    // HD by default, Full-HD if anything above is explicitly requested
    let resolution: 'hd' | 'full-hd' = 'hd';
    switch (this.resolutionPreference) {
      case 'full-hd':
        resolution = 'full-hd';
    }

    // @ts-ignore
    const configuration: Configuration = {
      selector: hostElement,
      engine: {
        symbologies: ['code128'],
        minScanlinesNeeded: 2,
        invertedCodes: false,
        duplicateInterval: this.duplicateFilterInterval
      },
      locator: {
        regionOfInterest: {
          left: 0.15,
          right: 0.15,
          top: 0.25,
          bottom: 0.25
        },
        chromaReject: false
      },
      frameSource: {
        resolution: resolution,
        rememberCameraDeviceId: true
      },
      overlay: {
        showCameraSelector: true,
        showFlashlight: true,
        showDetections: false
      },
      feedback: {
        audio: true,
        vibration: true
      }
    };

    // for devices with very low specs (e.g. CAT S42, we use low resolution)
    if (this.config.lowPowerScanning) {
      configuration.frameSource.resolution = 'hd';
      console.warn(`Low-power scanning enabled`);
    }
    return configuration;
  }

  pauseScanning(): void {
    console.debug(`> ScannerService.pauseScanning()`);
    this.scanningPaused.next(true);
  }

  resumeScanning(): void {
    console.debug(`> ScannerService.resumeScanning()`);
    this.scanningPaused.next(false);
  }
}
